import { api } from '../api.js';
import { state, setVoice } from '../state.js';
import { interview as store } from '../interview-store.js';
import { navigate, route, RouterLink } from '../router.js';
import { mmss, plural, Markdown } from '../ui.js';
import { VoiceInput, Voice, heardHeader, voiceSupported } from '../speech.js';

// Экран собеседования. Всё лишнее убрано намеренно: во время разговора на
// экране должны быть только реплики, микрофон и часы.

export const Interview = {
  components: { RouterLink, Markdown },
  data() {
    // Ручки живого экрана: микрофон, озвучка, таймеры, «думает». Данные собеса
    // — в interview-store.js.
    return {
      status: '',
      error: '',
      listening: false,
      level: [],
      thinking: false,
      hint: '',
      answerS: 0,
      answerTimer: null,
      capturing: null,
      finishing: false,
      finishS: 0,
      wrapTimer: null,
      awaitTimer: null,
      finishTimer: null,
      voiceIn: null,
      voiceOut: new Voice(),
      ticker: null,
    };
  },
  watch: {
    // Текст приходит и с клавиатуры, и с распознавания. На @input ловится
    // только первое: распознанный ответ разворачивал поле лишь после того, как
    // его тронут руками, и видно было полторы строки из четырёх.
    draft() { this.$nextTick(() => this.growSay()); },
  },
  computed: {
    // Переходники к стору. Черновик здесь не для красоты: человек наговорил
    // минуту, текст распознался — и терять его из-за случайного перехода
    // нельзя.
    session: { get() { return store.info; }, set(v) { store.info = v; } },
    project: { get() { return store.project; }, set(v) { store.project = v; } },
    turns: { get() { return store.turns; }, set(v) { store.turns = v; } },
    draft: { get() { return store.draft; }, set(v) { store.draft = v; } },
    code: { get() { return store.code; }, set(v) { store.code = v; } },
    elapsed: { get() { return store.elapsed; }, set(v) { store.elapsed = v; } },
    codeMode: { get() { return store.codeMode; }, set(v) { store.codeMode = v; } },
    awaiting: { get() { return store.awaiting; }, set(v) { store.awaiting = v; } },
    wrapping: { get() { return store.wrapping; }, set(v) { store.wrapping = v; } },
    showSetup: { get() { return store.showSetup; }, set(v) { store.showSetup = v; } },

    /** Сколько слов в черновике: односложный ответ на собесе — это провал. */
    draftWords() {
      const n = this.draft.trim().split(/\s+/).filter(Boolean).length;
      return `${n} ${plural(n, 'слово', 'слова', 'слов')}`;
    },
    plannedMin() { return this.session?.planned_min || 60; },
    overtime() { return this.elapsed > this.plannedMin * 60; },
    /** Длинный ответ — двухминутный: дальше на собесе начинают скучать. */
    answerLong() { return this.answerS > 120; },
    working() { return this.thinking || this.finishing; },
    duo() { return this.session?.duo || null; },
    setupText() { return this.session?.setup_visible || ''; },
    setupTitle() {
      return { incident: 'Что случилось', review: 'Файл на ревью' }[this.session?.setup_kind]
        || 'Вводная';
    },
    codingOn() { return this.session?.coding === 'enabled'; },
    // В форматах со своим сценарием секции кодирования нет — и кнопки тоже.
    codingAvailable() {
      return !['incident', 'review', 'pressure', 'drill', 'screening'].includes(this.session?.kind);
    },
    kindName() {
      const k = state.kinds.find((x) => x.id === this.session?.kind);
      return k ? k.name : this.session?.kind || '';
    },
    voices() { return state.tts.voices; },
    speeds() { return state.tts.speeds; },
    voice: {
      get() { return state.voice || state.tts.voice; },
      set(v) { setVoice(v, undefined); },
    },
    speed: {
      get() { return state.speed || state.tts.speed; },
      set(v) { setVoice(undefined, v); },
    },
  },
  async created() {
    // Мягкая ошибка ломает только черновик по ходу речи: запись идёт, и текст
    // расшифруется на сервере. Красным её показывать нельзя — человек решит,
    // что собес сломался.
    this.voiceIn = new VoiceInput(
      (bands) => { this.level = bands; },
      (m, soft) => { if (soft) this.hint = m; else this.error = m; });
    await this.load();
  },
  beforeUnmount() {
    clearInterval(this.ticker);
    clearInterval(this.answerTimer);
    clearInterval(this.finishTimer);
    clearTimeout(this.wrapTimer);
    clearTimeout(this.awaitTimer);
    this.voiceOut.stop();
    this.voiceIn?.stop();
  },
  methods: {
    mmss,

    /**
     * Поле растёт под текст. Распознанный ответ — это три-четыре строки живой
     * речи, и вычитывать их в щели высотой в одну строку невозможно: раньше
     * было видно последние полторы, а начало приходилось искать прокруткой.
     */
    growSay() {
      const el = this.$refs.say;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.34)}px`;
    },

    async load() {
      const id = Number(route.params.id);
      try {
        const data = await api.get(`/api/interview/${id}`);

        // Закрытый собес разговаривать уже не даёт: отправляем к разбору,
        // иначе человек видит кнопку «Закончить», которая вернёт 409.
        if (data.session.status === 'finished') {
          state.activeSession = null;
          return navigate(`/session/${data.session.id}`, true);
        }

        // «Закончить» нажали, разбор пишется — но собес ещё числится идущим.
        // Раньше сюда возвращались и получали живой экран с микрофоном, как
        // будто ничего не нажимали: второе «Закончить» отвечало отказом, а
        // разговаривать было уже не с кем. Ждём здесь и уходим к разбору сами.
        if (data.session.wrap === 'analysis') {
          store.take(data);
          this.wrapping = true;
          state.activeSession = null;
          clearTimeout(this.wrapTimer);
          this.wrapTimer = setTimeout(() => this.load(), 4000);
          this.loading = false;
          return;
        }
        this.wrapping = false;

        store.take(data);
        state.activeSession = data.session.status === 'finished' ? null : data.session;
        this.elapsed = data.elapsed_s || 0;

        if (!voiceSupported) {
          this.push('system', 'Браузер не умеет записывать звук — отвечать можно текстом.');
        }
        if (this.session.status === 'active') {
          this.ticker = setInterval(() => { this.elapsed += 1; }, 1000);
          const last = this.turns[this.turns.length - 1];

          // Собес открыт, а интервьюер ещё не сказал ни слова: приветствие
          // пишется дольше обычной реплики, и в него можно въехать раньше, чем
          // оно доедет. Раньше в этот момент просто включался микрофон —
          // человек говорил в пустоту, а интервьюер потом отвечал «повторяю».
          if (!this.turns.length) {
            this.awaiting = true;
            this.status = 'интервьюер начинает — сейчас поздоровается';
            clearTimeout(this.awaitTimer);
            this.awaitTimer = setTimeout(() => this.load(), 3000);
            return;
          }
          this.awaiting = false;

          // Продолжаем прерванный собес: последняя реплика уже сказана.
          if (last?.role === 'interviewer' && this.turns.length === 1) this.speak(last.body);
          else this.startListening();
        }
        this.scrollDown();
      } catch (e) {
        this.error = e.message;
      }
    },

    push(role, body, kind = 'speech', speaker = null) {
      this.turns.push({ role, body, at_s: this.elapsed, kind, speaker });
      this.scrollDown();
    },

    /**
     * Кладёт ответ интервьюера в ленту. Когда ведущих двое, сервер отдаёт его
     * уже разрезанным по говорящим — рисуем раздельными пузырями, иначе на
     * экране один безымянный монолог, и непонятно, кто сейчас спрашивал.
     */
    pushParts(reply, parts) {
      const said = Array.isArray(parts) && parts.length
        ? parts : [{ speaker: null, text: reply }];
      for (const p of said) this.push('interviewer', p.text, 'speech', p.speaker);
      return said;
    },

    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.thread;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    ttsUrl(part, voice) {
      const q = new URLSearchParams({ text: part, voice: voice || this.voice, speed: this.speed });
      return `/api/media/sessions/${this.session.id}/tts?${q}`;
    },

    /**
     * Чьим голосом читать реплику. Когда ведущих двое, голоса закреплены за
     * ними на старте: один и тот же тембр на обоих делает разговор кашей, а
     * ради этого различия формат и затевался.
     */
    /** Есть ли в реплике блок вывода: такие рисуем моноширинным, а не текстом. */
    hasBlock(t) {
      return t.role === 'interviewer' && /```/.test(t.body || '');
    },

    voiceFor(speaker) {
      if (!this.duo || !speaker) return this.voice;
      return speaker === this.duo.second.name ? this.duo.second.voice : this.duo.first.voice;
    },

    /** Озвучивает реплики подряд, каждую своим голосом, и включает микрофон. */
    async speakParts(parts) {
      for (const p of parts) {
        if (this.finishing) return;
        await this.speakOne(p.text, this.voiceFor(p.speaker));
      }
      if (!this.finishing) this.startListening();
    },

    speak(text) {
      return this.speakParts([{ speaker: null, text }]);
    },

    async speakOne(raw, voice) {
      // Вывод команд в разборе инцидента приходит блоком: его читают глазами,
      // а синтезатор превратил бы в набор звуков на минуту. Озвучиваем только
      // то, что вокруг блока, — роль как раз обязана оставлять реплику
      // понятной и без него.
      const text = String(raw).replace(/```[\s\S]*?```/g, ' ').replace(/\s+/g, ' ').trim();
      // Разбор длинный и читается глазами — озвучивать его незачем.
      if (!text || text.length > 700) return;
      this.status = 'озвучиваю…';
      try {
        await this.voiceOut.speak(text, (p) => this.ttsUrl(p, voice));
      } catch {
        if (this.finishing) return;
        this.status = '';
        return new Promise((done) => this.voiceOut.browserSay(text, done));
      }
      // Кнопку «Закончить» жмут как раз посреди озвучки — тогда speakOne() тут
      // и просыпается. Своё «озвучиваю» он снимать уже не вправе: на экране
      // висит статус завершения, и затирать его нельзя.
      if (this.finishing) return;
      this.status = '';
    },

    async startListening() {
      if (this.listening || this.finishing) return;
      // Интервьюер замолкает, как только человек берёт слово. Это не вежливость:
      // микрофон пишет сырой сигнал, без подавления эха (см. speech.js), и его
      // голос из колонок лёг бы прямо в запись ответа — а оттуда в расшифровку
      // как чужие слова. Перебивать при этом можно и нужно: живого собеседника
      // тоже перебивают.
      this.voiceOut.stop();
      this.listening = true;
      // Часы ответа идут по настоящему времени, а не по часам собеса: в режиме
      // разбора те стоят, а говорить там всё ещё можно.
      this.answerS = 0;
      clearInterval(this.answerTimer);
      this.answerTimer = setInterval(() => { this.answerS += 1; }, 1000);
      await this.voiceIn.start();

      // Браузер спрашивает доступ к микрофону не мгновенно, и собес за это
      // время могли закончить. Раньше запись всё равно стартовала — уже после
      // того, как finish() её остановил, — и на экране до конца висело
      // «запись идёт», хотя собес закрывали.
      if (this.finishing || !this.listening) return this.stopVoice();

      this.status = 'слушаю — договаривай и жми «Стоп»';
    },

    /** Заглушить всё, что говорит и слушает. Общее для завершения и отмены. */
    stopVoice() {
      this.listening = false;
      this.level = [];
      clearInterval(this.answerTimer);
      this.answerS = 0;
      this.voiceIn?.stop();
      this.voiceOut.stop();
    },

    /**
     * Останавливает запись и уточняет текст. Отправка обязана это дождаться:
     * иначе уйдёт черновик, а точный текст прилетит уже в очищенное поле.
     */
    stopListening() {
      if (!this.capturing) {
        this.capturing = this.doStop().finally(() => { this.capturing = null; });
      }
      return this.capturing;
    },

    async doStop() {
      this.listening = false;
      this.level = [];
      clearInterval(this.answerTimer);
      const said = await this.voiceIn.stop();
      // «Стоп», а сразу следом «Закончить»: расшифровка вернётся уже после, и
      // свой статус ей писать некуда — на экране идёт сборка разбора.
      if (this.finishing) return;
      if (!said) { this.status = ''; return; }
      this.status = 'уточняю распознавание…';
      try {
        // heard — что услышал сам браузер, третьим движком. Едет заголовком,
        // а не в адресе: путь должен оставаться цельным, иначе его перестаёт
        // видеть карта связей. Кириллица в заголовке — только в процентах.
        const { text } = await api.raw(
          `/api/media/sessions/${this.session.id}/stt`, said.blob, heardHeader(said.heard));
        if (this.finishing) return;
        if (text) {
          // Было что-то напечатано до записи — дописываем, а не затираем:
          // человек мог начать текстом и договорить голосом.
          const had = this.draft.trim();
          this.draft = had ? `${had} ${text}` : text;
          this.status = 'распознал — проверь и отправляй';
          this.$nextTick(() => this.growSay());
        }
        else this.status = 'ничего не расслышал — попробуй ещё раз';
      } catch (e) {
        this.status = 'распознавание не вышло: ' + e.message;
      }
    },

    async send() {
      if (this.listening || this.capturing) {
        this.status = 'дожидаюсь распознавания…';
        await this.stopListening();
      }
      const text = this.draft.trim();
      if (!text) { this.status = 'пусто — нечего отправлять'; return; }

      this.voiceOut.stop();
      this.draft = '';
      this.$nextTick(() => this.growSay());
      this.push('user', text);
      this.thinking = true;
      this.status = 'думает…';
      try {
        const { reply, parts, understood } = await api.durable(`/api/interview/${this.session.id}/turn`,
          { text, voice: this.voice },
          { onRetry: (n, err) => { this.status = `${err.message} — повторяю (${n})`; } });
        const said = this.pushParts(reply, parts);
        this.mark(understood);
        this.status = '';
        this.speakParts(said);
      } catch (e) {
        // Ответа не будет: возвращаем реплику в поле, чтобы не набирать её
        // заново, и убираем пузырь — в базе она тоже не осталась.
        this.turns.pop();
        this.draft = text;
        this.error = e.message;
        this.status = 'реплика не ушла — попробуй отправить ещё раз';
      } finally {
        this.thinking = false;
      }
    },

    /**
     * Кладёт на реплику кандидата то, как её понял интервьюер.
     *
     * Реплика уже в ленте — пузырь появился до ответа, — поэтому ищем
     * последнюю свою и дописываем. Показывается она не сама: пузырь
     * переворачивается по клику, когда есть сомнение в распознавании.
     */
    mark(understood) {
      if (!understood) return;
      for (let i = this.turns.length - 1; i >= 0; i -= 1) {
        if (this.turns[i].role === 'user') { this.turns[i].understood = understood; return; }
      }
    },

    /**
     * Переворот реплики: на обороте — как её услышал интервьюер.
     *
     * Половину анимации пузырь стоит ребром — в этот момент и подменяем текст,
     * иначе видно, как он меняется на лету, и это читается как сбой.
     */
    flip(t) {
      if (!t.understood || t.turning) return;
      t.turning = true;
      setTimeout(() => { t.flipped = !t.flipped; }, 130);
      setTimeout(() => { t.turning = false; }, 280);
    },

    /** Отправка написанного кода: озвучивать его незачем, это не речь. */
    async sendCode() {
      const text = this.code.trim();
      if (!text) { this.status = 'пустой код'; return; }
      this.voiceOut.stop();
      this.push('user', text, 'code');
      this.code = '';
      this.thinking = true;
      this.status = 'смотрит код…';
      try {
        const { reply, parts, understood } = await api.durable(`/api/interview/${this.session.id}/turn`,
          { text, voice: this.voice, kind: 'code' },
          { onRetry: (n, err) => { this.status = `${err.message} — повторяю (${n})`; } });
        const said = this.pushParts(reply, parts);
        this.mark(understood);
        this.status = '';
        this.speakParts(said);
      } catch (e) {
        this.turns.pop();
        this.code = text;
        this.error = e.message;
        this.status = 'код не ушёл — попробуй ещё раз';
      } finally {
        this.thinking = false;
      }
    },

    async skipCoding() {
      if (!confirm('Пропустить задачу на код? В оценки она не пойдёт.')) return;
      await api.post(`/api/interview/${this.session.id}/skip-coding`);
      this.session.coding = 'skipped';
      this.codeMode = false;
      this.push('system', 'Секция кодирования пропущена — в разборе её не будет.');
    },

    toggleMic() {
      this.voiceOut.stop();
      return this.listening ? this.stopListening() : this.startListening();
    },

    async finish() {
      if (!confirm('Закончить собеседование и получить разбор?')) return;
      // finishing поднимаем первым: на него смотрят startListening и speak,
      // и без этого запись успевала стартовать уже после остановки.
      this.finishing = true;
      this.stopVoice();
      clearInterval(this.ticker);
      // Счётчик, а не обещание. Разбор — это три прохода агента подряд, и по
      // живым замерам он занимает от минуты до трёх. Прежнее «до минуты» было
      // просто неправдой, а неправда на экране хуже долгого ожидания: человек
      // решает, что зависло, и уходит с середины.
      this.finishS = 0;
      clearInterval(this.finishTimer);
    clearTimeout(this.wrapTimer);
      this.finishTimer = setInterval(() => {
        this.finishS += 1;
        this.status = `собираю разбор и склеиваю запись… ${this.finishS}с`;
      }, 1000);
      this.status = 'собираю разбор и склеиваю запись…';
      this.push('system', 'Собес закончен. Собираю разбор и склеиваю запись — это несколько минут.');
      try {
        const { reply } = await api.durable(`/api/interview/${this.session.id}/finish`, {},
          { onRetry: (n, err) => { this.status = `${err.message} — повторяю (${n})`; } });
        // Раньше здесь начиналось дообсуждение, и разбор открывался отдельной
        // кнопкой. Шага не стало: уходим прямо на страницу разбора.
        state.activeSession = null;
        navigate(`/session/${this.session.id}`);
      } catch (e) {
        this.error = e.message;
        this.status = 'разбор не собрался — можно повторить';
      } finally {
        clearInterval(this.finishTimer);
    clearTimeout(this.wrapTimer);
        this.finishTimer = null;
        this.finishing = false;
      }
    },

    /**
     * Отмена без разбора. Нужна, когда разговор просто не задался: не тот тип
     * собеса, не тот характер, пора уходить. Агент не дёргается, сборка разбора
     * не запускается.
     */
    async cancel() {
      const said = this.turns.some((t) => t.role === 'user');
      const warn = said
        ? 'Отменить собеседование? Разбора не будет, стенограмма останется.'
        : 'Отменить собеседование? Ты ещё ничего не сказал — собес удалится целиком.';
      if (!confirm(warn)) return;

      this.finishing = true;
      this.stopVoice();
      clearInterval(this.ticker);
      this.status = 'закрываю собес…';
      try {
        const { deleted } = await api.post(`/api/interview/${this.session.id}/cancel`);
        state.activeSession = null;
        navigate(deleted ? `/project/${this.project.id}` : `/session/${this.session.id}`);
      } catch (e) {
        this.error = e.message;
        this.finishing = false;
      }
    },

  },
  template: `
    <div class="interview">
      <header>
        <router-link :to="project ? '/project/' + project.id : '/projects'" class="back" title="К проекту">
          ← {{ project?.name || 'Назад' }}
        </router-link>
        <span class="tag" v-if="session">
          {{ kindName }}<template v-if="session.seq"> · #{{ session.seq }}</template>
        </span>

        <span class="spacer"></span>

        <select class="inline" v-model="voice" v-if="voices.length && !duo"
                title="голос интервьюера">
          <option v-for="v in voices" :key="v.id" :value="v.id">{{ v.name }}</option>
        </select>
        <span class="tag" v-if="duo" title="их двое, у каждого свой голос">
          {{ duo.first.name }} и {{ duo.second.name }}
        </span>
        <select class="inline" v-model="speed" v-if="voices.length" title="скорость речи">
          <option v-for="s in speeds" :key="s" :value="s">×{{ s }}</option>
        </select>

        <span class="clock" :class="{ over: overtime }">
          {{ mmss(elapsed) }} <span class="faint">/ {{ plannedMin }}:00</span>
        </span>

        <button class="ghost sm" @click="cancel" :disabled="finishing"
                title="уйти без разбора">
          Отменить
        </button>
        <button class="danger" @click="finish" :disabled="finishing || wrapping">
          Закончить
        </button>
      </header>

      <div class="setup" :class="{ open: showSetup }" v-if="setupText">
        <div class="setup-head" @click="showSetup = !showSetup">
          <b>{{ setupTitle }}</b>
          <span class="faint">{{ showSetup ? 'закрыть' : 'развернуть' }}</span>
        </div>
        <div class="setup-body" v-show="showSetup">
          <Markdown :text="setupText"></Markdown>
        </div>
      </div>
      <!-- Подложка закрывает вводную кликом мимо: развёрнутая она лежит
           поверх разговора, и выход из неё должен быть очевидным. -->
      <div class="setup-back" v-if="setupText && showSetup" @click="showSetup = false"></div>

      <p class="err" v-if="session && session.setup_failed">
        Сценарий для этого формата собрать не удалось — собес пойдёт как обычный
        разговор. Лучше отменить и начать заново.
      </p>

      <div class="thread" ref="thread">
        <div class="thread-inner">
          <!-- Пока сказана только одна реплика, полэкрана всё равно пустует.
               Пусть это место объяснит, как тут говорят: на первом собесе
               человек не знает, что текст можно править перед отправкой. -->
          <div class="how" v-if="turns.length <= 1 && !codeMode">
            <b>Отвечать голосом</b>
            <span>Нажми «Говорить» и отвечай вслух. По «Стоп» речь появится в
              поле — там её можно поправить и отправить. Или просто напиши
              текстом. Клик по своей реплике покажет её же без ошибок распознавания.</span>
          </div>
          <div v-for="(t, i) in turns" :key="i" class="bubble"
               :class="[t.role, { code: t.kind === 'code', turning: t.turning,
                                  back: t.flipped, askable: t.understood,
                                  second: t.speaker && duo && t.speaker === duo.second.name }]"
               @click="t.role === 'user' && flip(t)"
               :title="t.understood && !t.flipped ? 'клик — как это выглядит без ошибок распознавания' : ''">
            <span class="who" v-if="t.speaker && duo">{{ t.speaker }}</span>
            <template v-if="t.flipped">
              <span class="who">расслышал так</span>{{ t.understood }}
            </template>
            <Markdown v-else-if="hasBlock(t)" :text="t.body" class="out"></Markdown>
            <template v-else>{{ t.body }}</template>
          </div>
          <p class="err" v-if="error">{{ error }}</p>
        </div>
      </div>

      <div class="composer" v-if="awaiting">
        <div class="composer-inner">
          <div class="status busy">
            <i class="dots"><b></b><b></b><b></b></i>
            <span>интервьюер начинает разговор — первая реплика пишется дольше остальных</span>
          </div>
        </div>
      </div>

      <div class="composer" v-else-if="wrapping">
        <div class="composer-inner">
          <div class="status busy">
            <i class="dots"><b></b><b></b><b></b></i>
            <span>собираю разбор — открою его сам, как будет готов</span>
          </div>
        </div>
      </div>

      <div class="composer" v-else>
        <div class="composer-inner">
          <div class="status" :class="{ busy: working, live: listening }">
            <i class="dots" v-if="status && (working || listening)"><b></b><b></b><b></b></i>
            <span>{{ status }}</span>
          </div>
          <!-- Поле идёт своей строкой и растёт под текст. Раньше оно делило
               строку с двумя кнопками и было высотой в одну строку: длинный
               ответ было видно на треть, а править распознанное приходилось
               вслепую, прокручивая внутри щели. -->
          <div class="say-box" v-if="!codeMode">
            <!-- Поле остаётся на месте всегда. Прятать его на время записи
                 было ошибкой: микрофон включается сам после реплики
                 интервьюера, и напечатать ответ становилось нельзя, пока не
                 нажмёшь «Стоп». Черновика от браузера здесь больше нет — он
                 калечил термины прямо во время ответа, — а уровень звука
                 показывается полосой рядом. -->
            <div class="hear" v-if="listening">
              <span class="wave" aria-hidden="true">
                <b v-for="(v, i) in (level.length ? level : [0,0,0,0,0])" :key="i"
                   :style="{ transform: 'scaleY(' + (0.12 + v * 0.88) + ')' }"></b>
              </span>
              <span>слушаю — текст появится, когда нажмёшь «Стоп»</span>
            </div>
            <textarea id="say" ref="say" v-model="draft" rows="2"
                      :placeholder="listening
                        ? 'можно и напечатать — запись идёт'
                        : 'скажи голосом или напиши здесь'"
                      @input="growSay" @keydown.enter.exact.prevent="send"></textarea>
            <div class="say-tools">
              <button class="mic" :class="{ recording: listening }" @click="toggleMic"
                      :disabled="thinking || finishing"
                      :title="listening ? 'закончить запись' : 'записать ответ голосом'">
                <span class="ico" aria-hidden="true" v-if="listening">
                  <svg viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="2"/></svg>
                </span>
                <span class="ico" aria-hidden="true" v-else>
                  <svg viewBox="0 0 16 16">
                    <rect x="6" y="2" width="4" height="7" rx="2"/>
                    <path d="M4 8a4 4 0 0 0 8 0M8 12v2"/>
                  </svg>
                </span>
                <template v-if="listening">
                  Стоп <span class="took" :class="{ long: answerLong }">{{ mmss(answerS) }}</span>
                </template>
                <template v-else>Говорить</template>
              </button>

              <button class="ghost sm" @click="codeMode = true" v-if="codingAvailable"
                      title="ответить кодом — распознавание не участвует">
                <span class="ico" aria-hidden="true">
                  <svg viewBox="0 0 16 16"><path d="M6 4 2.5 8 6 12M10 4l3.5 4L10 12"/></svg>
                </span>
                Код
              </button>

              <span class="faint sm" v-if="session && session.coding === 'skipped'">
                секция кодирования пропущена
              </span>

              <span class="spacer"></span>
              <span class="say-count" v-if="draft.trim()">{{ draftWords }}</span>
              <span class="say-count" v-else>Enter — отправить, Shift+Enter — перенос</span>
              <button class="primary" @click="send" :disabled="thinking || finishing || !draft.trim()">
                Отправить <kbd>⏎</kbd>
              </button>
            </div>
          </div>

          <div class="stack" v-else>
            <textarea id="code" v-model="code" spellcheck="false"
                      placeholder="код сюда — распознавание не участвует, пишешь руками"></textarea>
            <div class="row">
              <button class="primary" @click="sendCode" :disabled="thinking">Отправить код</button>
              <button class="ghost" @click="codeMode = false">Назад к голосу</button>
              <span class="spacer"></span>
              <button class="ghost sm" @click="skipCoding" v-if="codingOn">Пропустить задачу</button>
            </div>
          </div>

          <p class="faint hint" v-if="hint && !codeMode">{{ hint }}</p>
        </div>
      </div>
    </div>`,
};
